import React, { useState, useRef } from 'react';
import { ImageUploadResponse } from '../../services/widgetSettingsService';

interface AvatarUploaderProps {
  type: 'avatar' | 'logo';
  currentImageUrl?: string;
  onUpload: (file: File) => Promise<ImageUploadResponse>;
  onDelete: () => Promise<void>;
  className?: string;
}

const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  type,
  currentImageUrl,
  onUpload,
  onDelete,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxFileSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, or WebP)';
    }
    
    if (file.size > maxFileSize) {
      return 'File size must be less than 5MB';
    }
    
    return null;
  };

  const handleFileSelect = async (file: File) => {
    const error = validateFile(file);
    if (error) {
      alert(error);
      return;
    }

    setIsUploading(true);
    
    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      await onUpload(file);
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload ${type}. Please try again.`);
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
      return;
    }

    setIsDeleting(true);
    
    try {
      await onDelete();
      setPreviewUrl(null);
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Failed to delete ${type}. Please try again.`);
    } finally {
      setIsDeleting(false);
    }
  };

  const displayImageUrl = previewUrl || currentImageUrl;
  const typeLabel = type === 'avatar' ? 'Chatbot Avatar' : 'School Logo';

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {typeLabel}
        </label>
        <p className="text-xs text-gray-500 mb-4">
          Upload a {type === 'avatar' ? 'profile picture for your chatbot' : 'logo for your school'}. 
          Recommended size: 200x200px. Max file size: 5MB.
        </p>
      </div>

      {/* Image Preview */}
      {displayImageUrl && (
        <div className="space-y-3">
          <div className="relative inline-block">
            <div className="w-32 h-32 rounded-lg border-2 border-gray-300 overflow-hidden bg-gray-50">
              <img
                src={displayImageUrl}
                alt={typeLabel}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Delete Button */}
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs disabled:opacity-50"
              title={`Delete ${type}`}
            >
              {isDeleting ? '...' : '×'}
            </button>
          </div>
          
          {/* Change Image Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : 'Change Image'}
          </button>
        </div>
      )}

      {/* Upload Area - Only show when no image */}
      {!displayImageUrl && (
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isUploading ? (
            <div className="space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600">Uploading {type}...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-gray-400">
                <svg className="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              
              <div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Choose {type} file
                </button>
                <p className="text-gray-500">or drag and drop here</p>
              </div>
              
              <p className="text-xs text-gray-400">
                JPEG, PNG, GIF, WebP up to 5MB
              </p>
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Upload Guidelines */}
      <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-600">
        <p className="font-medium mb-1">Tips for best results:</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Use a square image (1:1 aspect ratio)</li>
          <li>Minimum size: 100x100px, recommended: 200x200px</li>
          <li>Keep the background simple or transparent</li>
          <li>Ensure good contrast for visibility</li>
        </ul>
      </div>
    </div>
  );
};

export default AvatarUploader;